import { documentSettingsOf } from '@batiflow/contracts';
import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CodePreview } from './code-preview';
import { useDocumentSettings } from './documents-api';
import { MetadataFields } from './metadata-fields';
import {
  EMPTY_METADATA,
  suggestFromFolders,
  titleFromFileName,
  type MetadataValues,
} from './naming-model';
import type { UploadExtras } from './upload-model';

/** Up to this many files each gets its own title field; a larger batch keeps the file names. */
export const MAX_TITLE_FIELDS = 20;

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"titles":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l","mlrwpw":"frs4mp9","Bmxbyg5":"f5zp4f","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f18qryls","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1bs9zne"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"}});

/** Where the files go: the project's code and the names of the folder and its ancestors. */
export interface NamingContext {
  /** Null in another organisation's space: its naming pattern is not read (no code preview). */
  orgId: string | null;
  /** The project's code, used by the `{PROJECT}` token. */
  projectCode: string | null;
  /** The open folder first, then its ancestors up to the root. */
  folderNames: readonly string[];
}

export interface UploadMetadataDialogProps {
  files: readonly File[];
  naming: NamingContext;
  /** "Add": one entry per file, in order, with the metadata of the form. */
  onConfirm: (extras: UploadExtras[]) => void;
  /** "Skip": the files are added without metadata (their codes come when metadata are set). */
  onSkip: () => void;
  onCancel: () => void;
}

/**
 * A short form for a batch of files that become new documents: discipline, phase and type
 * (suggested from the folder's name), zone, scale and a title per file, with the code the
 * organisation's pattern will give. Skipping adds the files without metadata.
 */
export function UploadMetadataDialog({
  files,
  naming,
  onConfirm,
  onSkip,
  onCancel,
}: UploadMetadataDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const settings = documentSettingsOf(useDocumentSettings(naming.orgId).data);

  const [values, setValues] = useState<MetadataValues>(() => ({
    ...EMPTY_METADATA,
    ...suggestFromFolders(naming.folderNames),
  }));
  const [titles, setTitles] = useState<string[]>(() =>
    files.map((file) => titleFromFileName(file.name)),
  );
  const editableTitles = files.length <= MAX_TITLE_FIELDS;

  const confirm = () => {
    onConfirm(
      files.map((file, index) => {
        const typed = titles[index]?.trim() ?? '';
        const title = typed === '' ? titleFromFileName(file.name) : typed;
        return {
          metadata: {
            title: title.slice(0, 300),
            ...(values.discipline ? { discipline: values.discipline } : {}),
            ...(values.phaseType ? { phaseType: values.phaseType } : {}),
            ...(values.docType ? { docType: values.docType } : {}),
            ...(values.zone.trim() ? { zone: values.zone.trim() } : {}),
            ...(values.scale.trim() ? { scale: values.scale.trim() } : {}),
          },
        };
      }),
    );
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('metadataDialog.title', { count: files.length })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.intro}>{t('metadataDialog.intro', { count: files.length })}</p>
              <MetadataFields
                values={values}
                onChange={(patch) => {
                  setValues((current) => ({ ...current, ...patch }));
                }}
              />
              <CodePreview settings={settings} projectCode={naming.projectCode} values={values} />
              {editableTitles ? (
                <div
                  className={styles.titles}
                  role="group"
                  aria-label={t('metadataDialog.titlesLabel')}
                >
                  {files.map((file, index) => (
                    <FormField
                      key={`${file.name}-${String(index)}`}
                      label={t('metadataDialog.titleOf', { name: isolate(file.name) })}
                    >
                      <Input
                        maxLength={300}
                        value={titles[index] ?? ''}
                        onChange={(_event, data) => {
                          setTitles((current) =>
                            current.map((title, at) => (at === index ? data.value : title)),
                          );
                        }}
                      />
                    </FormField>
                  ))}
                </div>
              ) : (
                <p className={styles.note}>{t('metadataDialog.titlesFromNames')}</p>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {t('common:actions.cancel')}
            </Button>
            <Button appearance="secondary" onClick={onSkip}>
              {t('metadataDialog.skip')}
            </Button>
            <Button appearance="primary" onClick={confirm}>
              {t('metadataDialog.add', { count: files.length })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/upload-metadata-dialog.griffel.css";